/**
 * EVO — usta dasturi ekranlarining URL holati.
 *
 * `lib/procurement-search.ts` bilan bir xil qoidalar:
 *   • URL ISHONCHSIZ KIRISH — har maydon tozalanadi (`?page=-5`,
 *     `?status=DROP TABLE` ekranni buzmaydi, default'ga tushadi);
 *   • DEFAULT qiymatlar URL ga YOZILMAYDI — manzil qisqa va ulashsa bo'ladigan;
 *   • chegaralar SERVERDAGI sxemalardan aynan ko'chirilgan
 *     (`loyalty-admin.dto.ts`), brauzer o'z qoidasini o'ylab topmaydi.
 *
 * Alohida fayl: bu ekranlar `procurement-search.ts` dagi xarid ekranlaridan
 * mustaqil va ularning importlarini (`lib/procurement.ts`, `lib/import-1c.ts`)
 * ortiqcha tortib kelmaydi.
 */
import { FRAUD_DEFAULTS, REDEMPTION_STATUSES, type RedemptionStatus } from './loyalty';

function positiveInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Chegaraga sig'gan butun son, aks holda `undefined` (default ishlaydi). */
function bounded(value: unknown, min: number, max: number): number | undefined {
  const parsed = positiveInt(value);
  return parsed !== undefined && parsed >= min && parsed <= max ? parsed : undefined;
}

// ---------------------------------------------------------------------------
// Ball qoidalari
// ---------------------------------------------------------------------------

export interface PointRuleSearch {
  readonly productId?: number;
  /**
   * Faqat bugun amal qilayotgan qoidalar.
   *
   * Default `true` va bu ONGLI: qoidalar sana bo'yicha versiyalanadi, ya'ni
   * bitta mahsulotda vaqt o'tishi bilan o'nlab qator yig'iladi. Ekranning
   * asosiy savoli esa «BUGUN qaysi ball beriladi?» — eski versiyalar
   * javobni ko'mib tashlaydi. Shuning uchun URL da `all=1` ko'rinadi,
   * `activeOnly` emas: default holat manzilda yozilmaydi.
   */
  readonly all?: boolean;
}

export function parsePointRuleSearch(search: Record<string, unknown>): PointRuleSearch {
  const productId = positiveInt(search.productId);
  return {
    ...(productId !== undefined ? { productId } : {}),
    ...(search.all === true || search.all === 'true' || search.all === '1' ? { all: true } : {}),
  };
}

// ---------------------------------------------------------------------------
// QR partiyalari
// ---------------------------------------------------------------------------

export interface QrBatchSearch {
  readonly page?: number;
  readonly productId?: number;
  /** Tasdiqlash qadami ochilgan partiya — eksport bir bosishda bo'lmasligi kerak. */
  readonly exportId?: number;
}

export function parseQrBatchSearch(search: Record<string, unknown>): QrBatchSearch {
  const page = positiveInt(search.page);
  const productId = positiveInt(search.productId);
  const exportId = positiveInt(search.exportId);

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(productId !== undefined ? { productId } : {}),
    ...(exportId !== undefined ? { exportId } : {}),
  };
}

// ---------------------------------------------------------------------------
// Almashtirishlar
// ---------------------------------------------------------------------------

export interface RedemptionSearch {
  readonly page?: number;
  readonly status?: RedemptionStatus;
  readonly installerId?: number;
}

export function parseRedemptionSearch(search: Record<string, unknown>): RedemptionSearch {
  const page = positiveInt(search.page);
  const installerId = positiveInt(search.installerId);
  const status =
    typeof search.status === 'string' &&
    (REDEMPTION_STATUSES as readonly string[]).includes(search.status)
      ? (search.status as RedemptionStatus)
      : undefined;

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(status ? { status } : {}),
    ...(installerId !== undefined ? { installerId } : {}),
  };
}

// ---------------------------------------------------------------------------
// Ustalar
// ---------------------------------------------------------------------------

export interface InstallerSearch {
  readonly page?: number;
  readonly q?: string;
  /** `true` — faqat bloklanganlar, `false` — faqat faollar, yo'q — hammasi. */
  readonly blocked?: boolean;
}

export function parseInstallerSearch(search: Record<string, unknown>): InstallerSearch {
  const page = positiveInt(search.page);
  const q = text(search.q);

  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(q !== undefined ? { q } : {}),
    ...(search.blocked === true || search.blocked === 'true'
      ? { blocked: true }
      : search.blocked === false || search.blocked === 'false'
        ? { blocked: false }
        : {}),
  };
}

// ---------------------------------------------------------------------------
// Firibgarlik navbati
// ---------------------------------------------------------------------------

export interface FraudSearch {
  readonly days?: number;
  readonly minAttempts?: number;
  readonly limit?: number;
}

/** Chegaralar `FraudQuerySchema` dan: 1..90, 2..1000, 1..200. */
export function parseFraudSearch(search: Record<string, unknown>): FraudSearch {
  const days = bounded(search.days, 1, 90);
  const minAttempts = bounded(search.minAttempts, 2, 1000);
  const limit = bounded(search.limit, 1, 200);

  return {
    ...(days !== undefined && days !== FRAUD_DEFAULTS.days ? { days } : {}),
    ...(minAttempts !== undefined && minAttempts !== FRAUD_DEFAULTS.minAttempts
      ? { minAttempts }
      : {}),
    ...(limit !== undefined && limit !== FRAUD_DEFAULTS.limit ? { limit } : {}),
  };
}
